import * as React from 'react';

import { css, keyframes } from '@compiled/react';
import { UniformRichTextNode } from '@uniformdev/canvas-react';
import {
	isRichTextValueConsideredEmpty,
	RichTextNode,
	TextNode,
	getRichTextTagsFromTextFormat,
	walkRichTextTree,
	ParameterRichTextValue,
} from '@uniformdev/richtext';
import { renderToStaticMarkup } from 'react-dom/server';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import wacToken from '@/libraries/design/foundations/token/src/wac-token';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';

import { blueShapesSVG, orangePurpleShapesSVG } from './design-refresh-heading-fill-svgs';

type EmphasisColor =
	| 'purple'
	| 'blue'
	| 'orange'
	| 'designRefresh.blues'
	| 'designRefresh.purpleOrange';

const EMPHASIS_COLOR_TO_HEX: Partial<Record<EmphasisColor, string>> = {
	purple: wacToken('color.primary.purple'),
	blue: wacToken('color.primary.blue'),
	orange: wacToken('color.extended.orange300'),
};

/**
 * Recolors any `<em>` (italic) inside the heading to whatever value the
 * `--wac-heading-emphasis-color` CSS variable holds. The variable itself
 * is set as an inline style on the heading based on the `emphasisColor`
 * prop, which keeps this rule fully static so Compiled can extract it
 * cleanly at build time.
 */
const emphasisColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	em: {
		color: 'var(--wac-heading-emphasis-color)',
		fontStyle: 'unset',
	},
});

const designRefreshEmphasisStyles = css({
	margin: 0,
	lineHeight: 1.1,
	fontSize: '4.5rem',
	marginBlockEnd: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	em: {
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent',
		backgroundClip: 'text',
		backgroundPosition: 'center -3rem',
		backgroundSize: '100% 200%',
		fontWeight: 900,
	},
	[media.below.md]: {
		fontSize: '2.5rem',
	},
});

const cssifySVG = (svgElement: React.ReactElement) => {
	const svgString = renderToStaticMarkup(svgElement);
	return 'url("data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString) + '")';
};

const designRefreshBluesStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	em: {
		backgroundColor: wacToken('color.extended.blue500'),
		backgroundImage: cssifySVG(blueShapesSVG()),
	},
});

const designRefreshPurpleOrangeStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	em: {
		backgroundColor: wacToken('color.extended.purple500'),
		backgroundImage: cssifySVG(orangePurpleShapesSVG()),
	},
});

/**
 * AWF1 theme emphasis: renders the emphasised portion as outline
 * (stroked) text — the text fill is transparent so the page background
 * shows through, and a border around each glyph is drawn using the
 * heading's own text color.
 *
 * The specific `emphasisColor` value is intentionally ignored; any
 * emphasis selection simply triggers the outline effect using the same
 * color as the rest of the heading text.
 */
const awf1EmphasisSlideIn = keyframes({
	from: {
		transform: 'translateX(-8px)',
	},
	to: {
		transform: 'translateX(0)',
	},
});

const awf1BlurReveal = keyframes({
	from: {
		opacity: 0,
	},
});

const awf1EmphasisStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'em:lang(en)': {
		display: 'inline-block',
		color: wacToken('color.neutral.dark100'),
		paintOrder: 'stroke fill',
		WebkitTextStrokeWidth: '.2rem',
		WebkitTextStrokeColor: 'var(--wac-color-heading)',
		fontStyle: 'unset',
		whiteSpace: 'nowrap',
		position: 'relative',
		// Left padding gives the blur room to bleed without
		// overlapping adjacent text. The blur extends leftward only,
		// so no right padding is needed.
		paddingInlineStart: token('space.100'),
		// Isolate stacking context so the ::before blur layer can sit
		// behind the text (z-index: -1) without escaping behind an
		// ancestor's stacking context.
		isolation: 'isolate',
		animationName: awf1EmphasisSlideIn,
		animationDuration: '175ms',
		animationDelay: '300ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'backwards',
		animationIterationCount: 1,
	},
});

/**
 * Motion-blur ("smear") pseudo-element behind the outlined `<em>`.
 * The `::before` duplicates the text content (via `data-text`), renders
 * it with the heading text color, and applies the SVG directional
 * Gaussian blur filter to create a horizontal speed-streak effect.
 * Positioned behind the outlined text via `z-index: -1`.
 */
const awf1EmphasisBlurStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'em:lang(en)::before': {
		content: 'attr(data-text)',
		position: 'absolute',
		insetInlineStart: 0,
		insetBlockStart: 0,
		color: 'var(--wac-color-heading)',
		WebkitTextStrokeWidth: 0,
		WebkitTextFillColor: 'var(--wac-color-heading)',
		filter: 'url(#hero-smear)',
		zIndex: -1,
		pointerEvents: 'none',
		animationName: awf1BlurReveal,
		animationDuration: '175ms',
		animationDelay: '300ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'backwards',
		animationIterationCount: 1,
		opacity: 0.6,
	},
});

/**
 * Hidden SVG containing the directional Gaussian blur filter used by
 * the AWF1 emphasis motion-blur effect.
 */
const Awf1SmearFilter = () => (
	<svg
		aria-hidden="true"
		focusable="false"
		css={css({
			position: 'absolute',
			width: 0,
			height: 0,
			overflow: 'hidden',
		})}
	>
		<defs>
			<filter
				id="hero-smear"
				x="-40%"
				y="-40%"
				width="180%"
				height="180%"
				colorInterpolationFilters="sRGB"
			>
				<feGaussianBlur stdDeviation="8 0.7" />
			</filter>
		</defs>
	</svg>
);

export interface UniformHeadingProps {
	text?: string;
	uniformRichText?: ParameterRichTextValue;
	// customRichText?: CustomRichTextConfig;
	htmlTagType?: 'h1' | 'h2';
	styleType?: 'h1' | 'h1-lg' | 'h1-lg-contrast' | 'h1-xl';
	alignment?: 'start' | 'center';
	wordWrap?: boolean;
	emphasisColor?: EmphasisColor;
}

// type WacTokenColor = "primary.black" | "primary.blue" | "primary.purple";

// interface CustomRichTextNode extends Record<string, unknown> {
//     type: "text" | "bold" | GestureLineType;
//     children?: CustomRichTextNode[];
//     text?: string;
//     version: number;
// }

// enum GestureLineType {
//     UNDERLINE_SQUIGGLE = "underline-squiggle",
//     EYELASH = "eyelash"
// }

// interface GestureLineNode extends CustomRichTextNode {
//     type: GestureLineType;
//     color: WacTokenColor;
//     children: CustomRichTextNode[];
// }

// interface CustomRichTextConfig {
//     content: CustomRichTextNode[];
//     plainText: string;
// }

const UniformHeading = ({
	text,
	uniformRichText,
	// customRichText,
	htmlTagType = 'h1',
	styleType = 'h1',
	alignment = 'start',
	wordWrap,
	emphasisColor,
}: // eslint-disable-next-line complexity
UniformHeadingProps) => {
	const { isDevTheme, isAwf1Theme } = useTheme();
	const useAwf1Blur = isAwf1Theme && !!emphasisColor;

	const isDesignRefreshEmphasisStyle =
		emphasisColor && emphasisColor.split('.').at(0) === 'designRefresh';

	const baseHeadingCss = css({
		marginBlock: token('space.0'),
		textAlign: alignment,
		maxWidth: '100%',
	});

	const headingCss = [
		baseHeadingCss,
		// Only recolor `<em>` when the author explicitly picked a color.
		// Otherwise let italics render with the browser default font-style
		// and inherited color.
		emphasisColor && !isAwf1Theme && emphasisColorStyles,
		!isAwf1Theme && isDesignRefreshEmphasisStyle && designRefreshEmphasisStyles,
		!isAwf1Theme && emphasisColor === 'designRefresh.blues' && designRefreshBluesStyles,
		!isAwf1Theme &&
			emphasisColor === 'designRefresh.purpleOrange' &&
			designRefreshPurpleOrangeStyles,
		// AWF1 theme: any emphasis color → outline (stroked) text effect
		isAwf1Theme && emphasisColor && awf1EmphasisStyles,
		useAwf1Blur && awf1EmphasisBlurStyles,
		wordWrap &&
			css({
				overflowWrap: 'break-word',
				textWrap: 'pretty',
			}),
	];

	// Inline-style overrides applied to the heading element. Sets the CSS
	// variable consumed by `emphasisColorStyles` (only when an emphasis
	// color is selected) and merges with the parent text color (if any)
	// so the heading itself still inherits container styling.
	//
	// Under AWF1 the emphasis stroke uses `currentColor` (inheriting the
	// heading text color), so the CSS variable is not needed.
	const headingStyle: React.CSSProperties = {
		...(emphasisColor && !isDesignRefreshEmphasisStyle && !isAwf1Theme
			? {
					['--wac-heading-emphasis-color' as never]: EMPHASIS_COLOR_TO_HEX[emphasisColor],
				}
			: null),
		color: 'var(--wac-color-heading)',
	};

	function resolveUniformRichText({
		node,
		children,
	}: {
		node: RichTextNode;
		children?: React.ReactNode;
	}) {
		const nodeIsBold = (node: RichTextNode): node is TextNode => {
			return (
				node.type === 'text' &&
				getRichTextTagsFromTextFormat((node as TextNode).format).includes('strong')
			);
		};

		const richTextValueHasBold = (value: RichTextNode): boolean => {
			let hasBold = false;
			walkRichTextTree(value, (node) => {
				if (nodeIsBold(node)) {
					hasBold = true;
				}
			});
			return hasBold;
		};

		const hasBoldText = richTextValueHasBold(node);
		const HeadingTag = htmlTagType;

		return (
			<HeadingTag
				css={headingCss}
				className={
					isDevTheme
						? 'mono-font-family-heading'
						: isAwf1Theme
							? 'sans-font-family-heading'
							: `${styleType} ${hasBoldText ? 'h1-contrast' : ''}`
				}
				style={headingStyle}
			>
				{useAwf1Blur && <Awf1SmearFilter />}
				{children}
			</HeadingTag>
		);
	}

	/**
	 * Custom text node resolver for AWF1 blur: when the text node has
	 * italic format (format & 2), render `<em data-text="...">` so the
	 * `::before` pseudo-element can duplicate the text for the blur
	 * layer. This is part of React's render tree, so it survives
	 * hydration — unlike imperative DOM manipulation via refs.
	 */
	function resolveAwf1TextNode({ node }: { node: RichTextNode }) {
		const textNode = node as TextNode;
		const tags = getRichTextTagsFromTextFormat(textNode.format);
		const isItalic = tags.includes('em');
		const isBold = tags.includes('strong');

		let content: React.ReactNode = <>{textNode.text}</>;
		if (isBold) {
			content = <strong>{content}</strong>;
		}
		if (isItalic) {
			content = <em data-text={textNode.text}>{content}</em>;
		}
		return content;
	}

	function resolveUniformRichTextRenderer(node: RichTextNode) {
		if (node.type === 'paragraph') {
			return resolveUniformRichText;
		}
		if (useAwf1Blur && node.type === 'text') {
			return resolveAwf1TextNode;
		}
		return undefined;
	}

	// const customRichTextIsConsideredEmpty = (
	//     customRichTextPropValue: CustomRichTextConfig | undefined
	// ) => {
	//     return (
	//         !customRichTextPropValue || customRichTextPropValue.plainText === ""
	//     );
	// };

	// const renderCustomRichText = () => {
	//     const nodeIsBold = (
	//         node: CustomRichTextNode
	//     ): node is CustomRichTextNode => {
	//         return node.type === "bold";
	//     };

	//     const walkCustomRichTextTree = (
	//         node: CustomRichTextNode,
	//         callBack: (node: CustomRichTextNode) => void
	//     ) => {
	//         callBack(node);
	//         if (node.children) {
	//             node.children.forEach((child) => {
	//                 walkCustomRichTextTree(child, callBack);
	//             });
	//         }
	//     };

	//     const customRichTextValueHasBold = (
	//         value: CustomRichTextNode
	//     ): boolean => {
	//         let hasBold = false;
	//         walkCustomRichTextTree(value, (node: CustomRichTextNode) => {
	//             if (nodeIsBold(node)) {
	//                 hasBold = true;
	//             }
	//         });
	//         return hasBold;
	//     };

	//     const hasBoldText = () => {
	//         let hasBold = false;
	//         customRichText?.content.forEach((node) => {
	//             if (customRichTextValueHasBold(node)) {
	//                 hasBold = true;
	//             }
	//         });
	//         return hasBold;
	//     };

	//     const HeadingTag = htmlTagType;

	//     const renderCustomRichTextNode = (
	//         node: CustomRichTextNode,
	//         index: number
	//     ) => {
	//         switch (node.type) {
	//             case "bold":
	//                 return (
	//                     <strong key={index}>
	//                         {node.children?.map((child, childIndex) =>
	//                             renderCustomRichTextNode(child, childIndex)
	//                         )}
	//                     </strong>
	//                 );
	//             case "underline-squiggle":
	//                 return (
	//                     <span
	//                         key={index}
	//                         css={css({
	//                             position: "relative"
	//                         })}
	//                     >
	//                         {getGestureLine(
	//                             node.type,
	//                             (node as GestureLineNode).color
	//                         )}
	//                         {node.children?.map((child, childIndex) =>
	//                             renderCustomRichTextNode(child, childIndex)
	//                         )}
	//                     </span>
	//                 );
	//             case "eyelash": {
	//                 return (
	//                     <>
	//                         <br />
	//                         <span
	//                             key={index}
	//                             css={css({ position: "relative" })}
	//                         >
	//                             {getGestureLine(
	//                                 node.type,
	//                                 (node as GestureLineNode).color
	//                             )}
	//                             {node.children?.map((child, childIndex) =>
	//                                 renderCustomRichTextNode(child, childIndex)
	//                             )}
	//                         </span>
	//                     </>
	//                 );
	//             }
	//             case "text":
	//             default:
	//                 return <span key={index}>{node.text || ""}</span>;
	//         }
	//     };

	//     return (
	//         <HeadingTag
	//             ref={headingRef as React.RefObject<HTMLHeadingElement>}
	//             css={headingCss}
	//             className={
	//                 isDarkTheme
	//                     ? "mono-font-family-heading"
	//                     : `${styleType} ${hasBoldText() ? "h1-contrast" : ""}`
	//             }
	//             style={headingStyle}
	//         >
	//             {customRichText?.content.map(
	//                 (node: CustomRichTextNode, index: number) =>
	//                     renderCustomRichTextNode(node, index)
	//             )}
	//         </HeadingTag>
	//     );
	// };

	const HeadingTag = htmlTagType;

	return !isRichTextValueConsideredEmpty(uniformRichText) ? (
		<UniformRichTextNode
			node={uniformRichText?.root as RichTextNode}
			resolveRichTextRenderer={resolveUniformRichTextRenderer}
		/>
	) : (
		// !customRichTextIsConsideredEmpty(customRichText) ? (
		//     renderCustomRichText() ) :
		<HeadingTag
			css={headingCss}
			className={
				isDevTheme
					? 'mono-font-family-heading'
					: isAwf1Theme
						? 'sans-font-family-heading'
						: styleType
			}
			style={headingStyle}
		>
			{useAwf1Blur && <Awf1SmearFilter />}
			{text}
		</HeadingTag>
	);
};

// const getGestureLine = (
//     gestureLine: GestureLineType,
//     gestureColor: WacTokenColor
// ) => {
//     switch (gestureLine) {
//         case "underline-squiggle":
//             return (
//                 <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     width="168"
//                     height="16"
//                     viewBox="0 0 168 16"
//                     fill="none"
//                     aria-hidden="true"
//                     css={css({
//                         position: "absolute",
//                         insetBlockEnd: token("space.negative.200"),
//                         insetInlineStart: "50%",
//                         transform: "translateX(-50%)"
//                     })}
//                 >
//                     <path
//                         d="M2.28772 11.6604C3.27772 12.0304 4.34772 11.9903 5.35772 11.8503C7.39772 11.5403 9.37772 10.8704 11.5877 10.1904C12.1377 9.84043 12.2377 9.88052 12.0577 9.53052C12.1777 9.40052 12.3777 9.17054 12.2377 9.05054C11.7477 8.23054 13.1677 7.92036 13.7077 7.62036C13.8777 8.38036 14.7577 8.34047 15.9677 7.98047C16.3177 7.88047 16.4477 7.83054 16.7777 7.80054C17.1377 7.75054 17.5177 7.69037 17.9177 7.63037C18.6977 7.55037 19.5677 7.43051 20.3677 7.52051C21.1777 7.59051 21.9477 7.82033 22.5477 8.34033C23.1577 8.85033 23.5977 9.61033 23.9377 10.3403C24.2777 11.0803 24.5277 11.7703 24.7977 12.3303C25.0477 12.8903 25.3077 13.2605 25.4377 13.4805C25.2677 13.7805 25.3077 14.2403 25.6877 14.5603C25.8477 14.7603 26.1177 14.8404 26.3877 14.9504C26.6777 15.0204 26.9977 15.0505 27.3177 15.0405C27.9777 14.9905 28.6077 14.7703 29.1477 14.6003C29.6277 14.4003 30.0777 14.2805 30.4177 14.2905C30.3777 14.3905 29.9477 14.8704 29.8977 14.9504C30.2677 15.0804 31.5877 14.5504 33.0877 13.6204C34.5877 12.6804 36.3277 11.3904 37.8377 10.1504C40.8877 7.70039 43.2477 5.65034 41.7577 7.85034C42.9077 7.23034 44.1177 6.56054 45.3277 5.80054C45.6277 5.61054 45.9377 5.42047 46.2477 5.23047L46.4577 5.10034L46.5577 5.05054C46.6177 5.02054 46.6877 4.98046 46.7477 4.97046H46.9577C46.9577 4.97046 47.0077 4.99048 47.0677 4.99048L47.3177 4.97046C47.6477 4.93046 47.9577 4.97046 48.2877 4.97046C49.5577 5.03046 50.7077 5.27036 51.5177 5.87036C52.3577 6.43036 53.0377 7.39032 53.8677 8.83032C54.2877 9.54032 54.7477 10.3605 55.4377 11.2405C56.1177 12.1105 57.1877 13.1003 58.5577 13.5903C58.8977 13.6903 59.2377 13.8104 59.5777 13.8804C59.9077 13.9304 60.2477 13.9705 60.5877 14.0005C61.2077 14.0005 61.8677 14.0004 62.4177 13.8804C62.6977 13.8304 62.9977 13.7905 63.2677 13.7205L64.0477 13.5005C64.5877 13.3605 65.0377 13.1605 65.5277 12.9805C69.2577 11.4205 72.3477 9.28033 75.4077 7.09033L77.3177 5.68042L78.2877 5.3103C78.6577 5.1603 78.9877 5.13052 79.3377 5.03052C79.5177 4.97052 79.6677 4.96045 79.8177 4.96045L80.2677 4.92041C80.4177 4.90041 80.5077 4.95044 80.6377 4.95044C80.7577 4.96044 80.8877 4.9505 80.9677 5.0105C81.3577 5.0805 81.7077 5.38037 82.1877 5.88037C83.1277 6.87037 84.1977 8.90048 86.2677 10.2405C86.7977 10.5505 87.3177 10.8705 87.8977 11.0505C88.1777 11.1505 88.4677 11.2603 88.7477 11.3403L89.6277 11.5005L90.1877 11.5903C90.3777 11.6103 90.5577 11.6904 90.7377 11.6204L91.7777 11.4004L93.0077 11.1504C99.5477 9.79039 105.978 9.92039 111.408 10.6504L111.248 10.4805C111.418 10.5405 111.958 10.4604 112.088 10.6704C113.168 10.3804 115.148 10.2204 116.858 10.1104C118.568 9.99035 120.008 9.98047 120.008 9.98047L126.178 9.68042C126.178 9.68042 128.828 10.0004 132.038 10.1804C135.248 10.3504 139.018 10.3505 141.168 9.75049C146.538 9.91049 151.978 9.90038 157.378 9.14038C157.378 9.14038 157.798 9.06042 158.458 8.93042C159.108 8.76042 159.998 8.50045 160.938 8.21045C161.878 7.90045 162.878 7.63048 163.728 7.24048C164.578 6.84048 165.308 6.46038 165.738 6.14038C166.818 5.34038 167.398 4.60032 167.398 4.08032C167.398 3.56032 166.798 3.26034 165.488 3.35034C164.838 3.40034 164.008 3.5403 162.978 3.8103C161.958 4.0903 160.678 4.30047 159.188 4.73047C159.108 4.69047 158.848 4.68034 158.798 4.60034C155.658 5.58034 152.388 5.81031 149.118 5.82031C147.488 5.85031 145.828 5.71044 144.178 5.70044C142.518 5.66044 140.838 5.55033 139.138 5.59033C139.218 5.88033 138.988 6.11045 138.788 5.96045L138.868 5.60034C134.278 6.20034 131.478 4.87037 127.188 5.88037C127.188 5.88037 127.698 5.43041 127.708 5.42041C124.088 5.28041 120.148 5.02035 115.968 4.86035C111.798 4.67035 107.398 4.61033 102.918 4.84033C101.798 4.92033 100.678 5.00032 99.5477 5.08032C98.4277 5.18032 97.2977 5.37051 96.1777 5.52051L94.4977 5.7605L92.8277 6.12036L91.1677 6.49048L90.4477 6.65039C90.5277 6.65039 90.4077 6.67039 90.3977 6.65039C90.3677 6.65039 90.3977 6.65037 90.3277 6.63037L90.0777 6.60034C89.8977 6.60034 89.7877 6.50046 89.6377 6.47046C89.3377 6.41046 89.1177 6.22034 88.8477 6.10034C88.3577 5.76034 87.8377 5.20043 87.2377 4.44043C86.6277 3.70043 85.9777 2.69047 84.7877 1.73047C84.5377 1.49047 84.1477 1.29032 83.8277 1.08032C83.4977 0.860322 83.1177 0.720332 82.7377 0.590332C81.9677 0.270332 81.2177 0.260439 80.4877 0.200439C80.5177 0.210439 80.5577 0.240498 80.5977 0.260498L80.5477 0.310303C80.8177 0.380303 81.2077 0.480342 81.6777 0.600342C82.1377 0.700342 82.7077 1.02054 83.2677 1.30054C84.3877 2.00054 85.2177 3.07043 85.7777 3.94043C86.3577 4.84043 86.7577 5.54052 87.1077 6.03052C87.4577 6.48052 87.6877 6.76048 87.6877 6.74048C86.5777 6.38048 85.6577 5.36052 84.6977 4.28052C84.1877 3.75052 83.6877 3.17048 82.9877 2.74048C82.3277 2.26048 81.5077 2.01037 80.6977 1.88037C79.8977 1.79037 79.0677 1.79041 78.3077 1.92041C77.4877 1.99041 76.8377 2.21041 76.0077 2.42041L75.6577 2.52051C75.5477 2.57051 75.4477 2.64045 75.3377 2.71045L74.8077 3.03052L73.9177 3.58032C73.3277 3.96032 72.7577 4.34047 72.1877 4.73047L71.9477 4.32031C72.9277 3.67031 73.7977 2.95035 74.5077 2.36035L74.6377 2.25049L74.8677 2.07031C75.0177 1.96031 75.1377 1.82049 75.2977 1.75049C75.5977 1.62049 75.8977 1.48039 76.0577 1.40039C76.4177 1.22039 76.7177 1.06042 76.9377 0.93042C76.6377 1.02042 76.3377 1.10043 76.0177 1.19043C75.8277 1.25043 75.6377 1.31036 75.4477 1.37036L75.0877 1.49048C74.9677 1.55048 74.8677 1.63044 74.7577 1.70044C74.2077 2.06044 73.6377 2.43031 73.0377 2.82031C71.9577 3.52031 70.7877 4.26048 69.5577 4.99048C68.3377 5.72048 67.0477 6.48035 65.7477 7.11035C64.4677 7.75035 63.1077 8.35031 61.9777 8.57031C61.4477 8.66031 60.9377 8.7304 60.6677 8.6604C60.3877 8.6004 60.2477 8.55031 59.9677 8.32031C59.6977 8.10031 59.3477 7.66033 58.9677 7.09033C58.5877 6.53033 58.1977 5.88043 57.7177 5.19043C56.8077 3.83043 55.4277 2.36054 53.8077 1.55054C52.1877 0.720537 50.5777 0.550342 49.3377 0.600342C49.3977 0.690342 49.6377 0.730479 49.9777 0.740479C50.3177 0.770479 50.7777 0.8304 51.2477 0.9104C51.7177 1.0204 52.2077 1.20043 52.5777 1.44043C52.9477 1.71043 53.2077 2.02036 53.2477 2.37036C52.0777 1.73036 50.7777 1.51044 49.5877 1.45044L48.6977 1.43042L47.8477 1.50049C47.5677 1.52049 47.2777 1.53032 47.0177 1.58032C46.6977 1.63032 46.3777 1.67046 46.0677 1.72046C46.0177 1.53046 46.0977 1.40053 46.2577 1.29053C46.3377 1.24053 46.4377 1.19039 46.5477 1.15039C46.6377 1.11039 46.7077 1.0903 46.7977 1.0603C47.1377 0.950303 47.5377 0.960381 47.6577 0.890381C47.5377 0.880381 47.3277 0.840352 47.0677 0.860352C46.9377 0.860352 46.7877 0.870371 46.6277 0.880371C46.3877 0.900371 46.1277 0.930439 45.8477 0.950439C45.6977 0.960439 45.5477 0.980488 45.3777 1.00049L45.1277 1.02051C45.0477 1.05051 44.9877 1.11039 44.9177 1.15039C44.6377 1.35039 44.3377 1.56053 44.0177 1.79053C43.6377 2.05053 43.2077 2.3404 42.7477 2.6604C40.9377 3.9104 38.5277 5.5904 35.7377 7.4104C34.3477 8.3304 32.8477 9.23033 31.3077 10.0903C30.9277 10.2903 30.5377 10.4804 30.1477 10.6804C29.7677 10.8504 29.3877 11.0204 28.9977 11.1904L28.4577 11.3704L28.1877 11.4705L28.0877 11.4905C27.9377 11.5305 27.8877 11.5305 27.8177 11.5205C27.6677 11.5205 27.5777 11.3805 27.5377 11.2505L27.2377 10.2505C27.0977 9.76049 26.9177 9.1703 26.6777 8.5603C26.4377 7.9403 26.0877 7.27035 25.6277 6.61035C25.3577 6.29035 25.0977 5.96041 24.7877 5.67041C24.4577 5.40041 24.1377 5.09038 23.7677 4.89038C22.2977 3.98038 20.7377 3.74048 19.4877 3.74048C18.1677 3.71048 17.0877 3.89035 15.9277 4.11035C15.7477 4.16035 15.5177 4.15049 15.3077 4.25049L14.7377 4.49048L13.9977 4.83032C13.5277 5.06032 13.0777 5.2805 12.6577 5.5105C10.9577 6.4105 9.43772 7.26043 8.02772 7.94043C6.62772 8.62043 5.32772 9.14035 4.32772 9.36035C3.83772 9.47035 3.42772 9.49046 3.18772 9.47046C2.95772 9.46046 2.81772 9.42033 2.64772 9.34033C2.36772 9.20033 2.02772 8.68046 1.82772 8.22046C1.59772 7.77046 1.46772 7.39051 1.29772 7.27051C1.13772 7.14051 0.99772 7.24036 0.86772 7.62036C0.73772 8.01036 0.61772 8.68038 0.80772 9.64038C0.63772 9.58038 0.44772 9.51041 0.28772 9.42041C0.58772 10.4404 1.30772 11.3504 2.30772 11.7004L2.28772 11.6604Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                 </svg>
//             );
//         case "eyelash":
//             return (
//                 <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     width="117"
//                     height="117"
//                     viewBox="0 0 117 117"
//                     fill="none"
//                     aria-hidden="true"
//                     css={css({
//                         position: "absolute",
//                         insetInlineEnd: `calc(100% - ${token("space.100")})`,
//                         insetBlockStart: token("space.negative.150"),
//                         width: "auto",
//                         height: "90%"
//                     })}
//                 >
//                     <path
//                         d="M25.93 74.8872C25.1564 75.4245 12.3829 73.151 16.0089 71.2258C17.2313 70.3577 18.3652 70.4522 18.7372 71.7455C19.127 69.2416 21.5364 72.5841 22.7056 71.7691C22.3041 70.0211 25.7765 73.6706 25.115 71.8518C38.9455 69.9857 66.6537 86.9815 32.7862 76.7356C36.1641 75.956 32.987 75.2593 31.4693 75.6431C33.6248 77.8813 27.306 75.9325 26.1426 75.1589C28.1032 75.2061 24.8788 73.5526 25.9359 74.8813L25.93 74.8872Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                     <path
//                         d="M51.0103 53.7517C49.2092 53.6395 41.6974 41.3562 40.7526 40.187C39.1994 37.311 43.345 37.0866 44.0123 39.5374C44.3489 39.9566 45.5123 43.4468 46.3568 41.9527C49.8823 42.7263 50.3548 48.9624 53.3311 49.3522C52.8528 50.173 53.9039 51.1533 53.6618 51.7025C54.5476 51.608 55.0377 52.795 54.8251 52.9013C58.5987 55.978 53.898 59.604 51.8784 55.1867C51.7248 55.3757 50.6973 54.5961 51.0102 53.7635L51.0103 53.7517Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                     <path
//                         d="M96.948 30.7506C98.5602 32.788 98.4539 30.6385 97.6566 29.6523C96.198 31.477 96.2098 27.2133 96.6999 26.1208C96.7885 26.5637 97.5562 26.6346 97.4263 25.7133C95.5838 26.5637 94.0779 14.9005 98.5661 19.991C98.5779 20.1918 97.0838 20.4457 97.5739 20.6287C99.8121 19.9851 98.4007 21.9989 98.2767 22.5363C99.4578 22.3945 98.4066 23.0087 98.9086 23.0855C100.438 22.2764 98.0641 25.5598 99.57 24.6681C102.245 27.0834 99.564 32.7408 101.962 34.5478C99.44 36.2545 104.241 44.3981 98.8437 41.6462C97.3378 38.0734 96.3988 35.1147 96.948 30.7506Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                     <path
//                         d="M67.6391 11.4579C71.2001 10.1291 71.1824 18.2727 72.8891 20.2983C75.2808 22.7136 85.4263 50.3746 77.5308 43.7665C74.5308 35.3689 70.6037 29.0087 68.2533 18.3672C67.7454 16.6664 65.0643 11.5051 67.6391 11.4579Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                     <path
//                         d="M43.5337 100.854C45.9077 103.617 48.5592 99.584 50.7796 101.828C54.2166 106.222 27.3351 110.663 27.7485 105.737C28.6757 101.551 41.7739 102.826 43.5337 100.854Z"
//                         fill={wacToken(`color.${gestureColor}`)}
//                     />
//                 </svg>
//             );
//         default:
//             return null;
//     }
// };

export default UniformHeading;
